import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateWorkOrderRequest,
  ISingleWorkOrderResponse,
  IUpdateWorkOrderStatusRequest,
} from "../models/WorkOrder";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

export default function useWorkOrderMutations() {
  const { post, put, patch, del } = useApiHelper();
  const queryClient = useQueryClient();

  const invalidateAll = (workOrderId?: string) => {
    queryClient.invalidateQueries(["workOrders"]);
    queryClient.invalidateQueries(["pagedTable", "/workorder/paginated"]);
    queryClient.invalidateQueries(["workOrderStats"]);
    if (workOrderId) {
      queryClient.invalidateQueries(["workOrder", workOrderId]);
    }
  };

  const create = useMutation(
    (request: ICreateWorkOrderRequest) =>
      post<ICreateWorkOrderRequest, ISingleWorkOrderResponse>("/workorder", request),
    {
      onSuccess: () => invalidateAll(),
    }
  );

  const update = useMutation(
    ({ workOrderId, request }: { workOrderId: string; request: ICreateWorkOrderRequest }) =>
      put<ICreateWorkOrderRequest, ISingleWorkOrderResponse>(`/workorder/${workOrderId}`, request),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const updateStatus = useMutation(
    ({ workOrderId, status }: { workOrderId: string; status: IUpdateWorkOrderStatusRequest }) =>
      patch<ISingleWorkOrderResponse>(`/workorder/${workOrderId}/status`, status),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const remove = useMutation(
    (workOrderId: string) => del<DefaultResponse>(`/workorder/${workOrderId}`),
    {
      onSuccess: () => invalidateAll(),
    }
  );

  return {
    create,
    update,
    updateStatus,
    remove,
  };
}
